import type { EdrSeverityLevel } from '../shared/edrTokens';
import type { ThreatHuntingState } from '../threat-hunting/useThreatHuntingData';
import {
  buildThreatHuntingAgentSummaryFromFindings,
  emptyThreatHuntingFacets,
  mergeThreatHuntingFacets,
  type ThreatHuntingFacets,
} from '../threat-hunting/threatHuntingDetailModel';
import type { SecuritySearchableFilterOption } from '../shared/SecuritySearchableFilter';
import type {
  WazuhThreatHuntingAgentSummaryData,
  WazuhThreatHuntingFindingItem,
} from 'src/types/mindtime/wazuhThreatHunting';
import { previewPaged } from './previewUtils';

export type ThreatHuntingPieData = {
  labels: string[];
  series: number[];
};

export type ThreatHuntingStat = {
  id: string;
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
};

export type ThreatHuntingResultRow = {
  id: string;
  timestamp: string;
  endpoint: string;
  hostName?: string;
  organization: string;
  detection: string;
  mitreTechnique: string;
  user: string;
  severity: EdrSeverityLevel;
  eventType: string;
  systemWazuhAgentId: string;
};

export const THREAT_HUNTING_KPI = {
  threatMatches: 31,
  matchesBySeverity: {
    critical: 2,
    high: 4,
    medium: 18,
    low: 7,
  },
} as const;

export const THREAT_HUNTING_RESULT_ROWS: ThreatHuntingResultRow[] = [
  {
    id: 'preview-threat-1',
    timestamp: '2026-08-06T12:45:00.000Z',
    endpoint: 'FIN-SRV-02',
    hostName: 'fin-srv-02.acme.local',
    organization: 'Acme Financial',
    detection: 'Encoded PowerShell Command',
    mitreTechnique: 'T1059',
    user: 'administrator',
    severity: 'high',
    eventType: 'process',
    systemWazuhAgentId: 'preview-endpoint-1',
  },
  {
    id: 'preview-threat-2',
    timestamp: '2026-08-06T12:18:00.000Z',
    endpoint: 'HR-PC-17',
    hostName: 'hr-pc-17.vertex.local',
    organization: 'Vertex Solutions',
    detection: 'Credential Dumping Attempt',
    mitreTechnique: 'T1003',
    user: 'j.smith',
    severity: 'critical',
    eventType: 'process',
    systemWazuhAgentId: 'preview-endpoint-2',
  },
  {
    id: 'preview-threat-3',
    timestamp: '2026-08-06T11:52:00.000Z',
    endpoint: 'APP-SERVER-01',
    hostName: 'app-server-01.nexus.local',
    organization: 'Nexus Dynamics',
    detection: 'Suspicious Scheduled Task',
    mitreTechnique: 'T1053',
    user: 'SYSTEM',
    severity: 'medium',
    eventType: 'process',
    systemWazuhAgentId: 'preview-endpoint-3',
  },
  {
    id: 'preview-threat-4',
    timestamp: '2026-08-06T10:30:00.000Z',
    endpoint: 'DB-SERVER-04',
    hostName: 'db-server-04.pinnacle.local',
    organization: 'Pinnacle Group',
    detection: 'Lateral Movement Detected',
    mitreTechnique: 'T1021',
    user: 'helpdesk',
    severity: 'high',
    eventType: 'network',
    systemWazuhAgentId: 'preview-endpoint-4',
  },
  {
    id: 'preview-threat-5',
    timestamp: '2026-08-06T09:14:00.000Z',
    endpoint: 'LAPTOP-443',
    hostName: 'laptop-443.acme.local',
    organization: 'Acme Financial',
    detection: 'Unusual Outbound Connection',
    mitreTechnique: 'T1071',
    user: 'm.chen',
    severity: 'medium',
    eventType: 'network',
    systemWazuhAgentId: 'preview-endpoint-5',
  },
];

const toFindingItem = (row: ThreatHuntingResultRow): WazuhThreatHuntingFindingItem => ({
  id: row.id,
  timestamp: row.timestamp,
  endpoint: row.endpoint,
  hostName: row.hostName,
  organization: row.organization,
  detection: row.detection,
  mitreId: row.mitreTechnique,
  mitreIds: [row.mitreTechnique],
  user: row.user,
  severity: row.severity,
  eventType: row.eventType,
  systemWazuhAgentId: row.systemWazuhAgentId,
});

export const getThreatHuntingPreviewUserOptions = (
  systemWazuhAgentId?: string
): SecuritySearchableFilterOption[] => {
  const agentId = systemWazuhAgentId?.trim();
  const rows = agentId
    ? THREAT_HUNTING_RESULT_ROWS.filter((row) => row.systemWazuhAgentId === agentId)
    : THREAT_HUNTING_RESULT_ROWS;

  return Array.from(new Set(rows.map((row) => row.user).filter(Boolean))).map((name) => ({
    label: name,
    value: name,
  }));
};

export const getThreatHuntingPreviewState = (): ThreatHuntingState => ({
  loading: false,
  summary: {
    threatMatches: THREAT_HUNTING_KPI.threatMatches,
    matchesBySeverity: { ...THREAT_HUNTING_KPI.matchesBySeverity },
  },
  items: previewPaged(THREAT_HUNTING_RESULT_ROWS.map(toFindingItem), 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        threatMatches: 8,
        matchesBySeverity: { critical: 2, high: 3, medium: 2, low: 1 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-1',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
        organization: 'Acme Financial',
        threatMatches: 5,
        matchesBySeverity: { critical: 0, high: 1, medium: 3, low: 1 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organization: 'Nexus Dynamics',
        threatMatches: 3,
        matchesBySeverity: { critical: 0, high: 0, medium: 2, low: 1 },
      },
    ],
    0,
    10,
  ),
  facets: {
    mitreIds: ['T1059', 'T1003', 'T1053', 'T1021', 'T1071'],
    eventTypes: ['process', 'network', 'file', 'registry'],
  },
});

export const getThreatHuntingDetailPreview = (id: string): WazuhThreatHuntingFindingItem | null => {
  const row = THREAT_HUNTING_RESULT_ROWS.find((entry) => entry.id === id) ?? THREAT_HUNTING_RESULT_ROWS[0];
  return row ? toFindingItem(row) : null;
};

export const getAgentThreatHuntingPreview = (
  systemWazuhAgentId: string,
): {
  summary: WazuhThreatHuntingAgentSummaryData;
  items: WazuhThreatHuntingFindingItem[];
  facets: ThreatHuntingFacets;
} => {
  const agentId = systemWazuhAgentId.trim();
  const items = THREAT_HUNTING_RESULT_ROWS.filter((row) => row.systemWazuhAgentId === agentId).map(
    toFindingItem
  );
  const list = items.length ? items : [toFindingItem(THREAT_HUNTING_RESULT_ROWS[0])];

  return {
    summary: buildThreatHuntingAgentSummaryFromFindings(agentId, list),
    items: list,
    facets: mergeThreatHuntingFacets(list, emptyThreatHuntingFacets()),
  };
};
